/* ============================================================================
   M0 — Update Rates & TAT wizard (route /supply/partners/update).
   Real backend flow: pick parent → account code (courierPartner/searchCached),
   choose scope + effective date, upload the rate-card CSV (WEIGHT_PRICE, scheduled
   via effectiveFrom) and/or TAT CSV (immediate), review, then commit →
   tools/upload/servicability per file (supplyApiSlice.uploadServicabilityFile).
   useReducer drives the step flow; the plan reshapes with the chosen scope.
   ============================================================================ */
import React, {useReducer, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {Button} from '@mui/material'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import CloseIcon from '@mui/icons-material/Close'
import PageTitle from '../../../components/PageTitle'
import {ConfirmDialog, WizardStepper, SupplyHero, HERO_BTN, useToast} from '../components'
import {useUploadServicabilityFileMutation} from '../../../redux/supplyApiSlice'
import {apiError} from '../supplyMeta'
import {defaultEff} from './wizardUtils'
import WizardStyles from './wizardStyles'
import StepPick from './StepPick'
import StepScope from './StepScope'
import StepRates from './StepRates'
import StepTat from './StepTat'
import StepReview from './StepReview'
import StepDone from './StepDone'

function initState(fromCode) {
  return {
    step: 0,
    fromCode: fromCode || '', // deep-link: StepPick preselects this code once the list loads
    partnerName: '',
    code: '',
    prozoId: '',
    display: '',
    scope: {rates: true, tat: true},
    effectiveFrom: defaultEff(),
    rateFile: null,
    tatFile: null,
    review: {reviewed: false},
    result: null,
  }
}

/* dynamic step plan — only the steps the scope needs */
function plan(scope) {
  const s = [
    {key: 'pick', label: 'Partner & Code'},
    {key: 'scope', label: 'What to Update'},
  ]
  if (scope.rates) s.push({key: 'rates', label: 'Upload Rates'})
  if (scope.tat) s.push({key: 'tat', label: 'Upload TAT'})
  s.push({key: 'review', label: 'Review & Commit'})
  s.push({key: 'done', label: 'Complete'})
  return s
}
const idxOf = (scope, key) => plan(scope).findIndex((p) => p.key === key)

function reducer(state, action) {
  switch (action.type) {
    case 'goto':
      return {...state, step: action.step}
    case 'next':
      return {...state, step: state.step + 1}
    case 'back':
      return {...state, step: state.step - 1}
    case 'pickParent':
      if (state.partnerName === action.name) return state
      return {...state, partnerName: action.name, code: '', prozoId: '', display: ''}
    case 'pickCode':
      return {...state, code: action.code, prozoId: action.prozoId, display: action.display}
    case 'setScope':
      return {...state, scope: action.scope}
    case 'setEff':
      return {...state, effectiveFrom: action.value}
    case 'setRateFile':
      return {...state, rateFile: action.file}
    case 'setTatFile':
      return {...state, tatFile: action.file}
    case 'setReview':
      return {...state, review: {...state.review, ...action.patch}}
    case 'setResult':
      return {...state, result: action.result}
    case 'reset':
      return initState(action.fromCode)
    default:
      return state
  }
}

export default function UpdateWizard() {
  const navigate = useNavigate()
  const toast = useToast()
  const [params] = useSearchParams()
  const fromCode = params.get('code') || ''
  const [U, dispatch] = useReducer(reducer, fromCode, initState)
  const [commitOpen, setCommitOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [uploadFile] = useUploadServicabilityFileMutation()

  const steps = plan(U.scope)
  const stepIdx = Math.min(U.step, steps.length - 1)
  const k = (steps[stepIdx] || {}).key
  const real = steps.filter((s) => s.key !== 'done')
  const total = real.length
  const scopeLbl = U.scope.rates && U.scope.tat ? 'Rates + TAT' : U.scope.rates ? 'Rates only' : 'TAT only'

  /* ── real commit: upload each attached file against the code's prozoId ──── */
  const finish = async () => {
    if (!U.prozoId) {
      toast('No prozoId for the selected code — re-pick the account code.', 'error')
      return
    }
    setBusy(true)
    try {
      if (U.scope.rates && U.rateFile) {
        await uploadFile({prozoId: U.prozoId, type: 'WEIGHT_PRICE', file: U.rateFile, effectiveFrom: U.effectiveFrom}).unwrap()
      }
      if (U.scope.tat && U.tatFile) {
        await uploadFile({prozoId: U.prozoId, type: 'TAT', file: U.tatFile}).unwrap()
      }
      dispatch({
        type: 'setResult',
        result: {
          rate: U.scope.rates && U.rateFile ? 1 : 0,
          tat: U.scope.tat && U.tatFile ? 1 : 0,
          code: U.display || U.code,
          prozoId: U.prozoId,
          effectiveFrom: U.effectiveFrom,
        },
      })
      toast('Uploaded to ProShip.', 'success')
      dispatch({type: 'goto', step: idxOf(U.scope, 'done')})
    } catch (e) {
      toast(apiError(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  const requestCommit = () => {
    const hasRate = U.scope.rates && U.rateFile
    const hasTat = U.scope.tat && U.tatFile
    if (!hasRate && !hasTat) {
      toast('Attach a rate card and/or TAT file first.', 'error')
      return
    }
    setCommitOpen(true)
  }

  const commitBullets = [
    U.scope.rates && U.rateFile
      ? 'Rate card → ' + U.prozoId + (new Date(U.effectiveFrom) > new Date() ? ' (pending, live on ' + U.effectiveFrom + ')' : ' (immediate)')
      : null,
    U.scope.tat && U.tatFile ? 'TAT → ' + U.prozoId + ' (immediate)' : null,
  ].filter(Boolean)

  const renderStep = () => {
    if (k === 'pick')
      return (
        <StepPick
          U={U}
          toast={toast}
          initialCode={U.fromCode}
          onPickParent={(name) => dispatch({type: 'pickParent', name})}
          onPickCode={(c) => dispatch({type: 'pickCode', code: c.code, prozoId: c.prozoId, display: c.display})}
          onNext={() => dispatch({type: 'next'})}
        />
      )
    if (k === 'scope')
      return (
        <StepScope
          U={U}
          onSetScope={(scope) => dispatch({type: 'setScope', scope})}
          onBack={() => dispatch({type: 'back'})}
          onNext={() => dispatch({type: 'next'})}
        />
      )
    if (k === 'rates')
      return (
        <StepRates
          U={U}
          onSetRateFile={(file) => dispatch({type: 'setRateFile', file})}
          onSetEff={(value) => dispatch({type: 'setEff', value})}
          onBack={() => dispatch({type: 'back'})}
          onNext={() => dispatch({type: 'next'})}
        />
      )
    if (k === 'tat')
      return (
        <StepTat
          U={U}
          onSetTatFile={(file) => dispatch({type: 'setTatFile', file})}
          onBack={() => dispatch({type: 'back'})}
          onNext={() => dispatch({type: 'next'})}
        />
      )
    if (k === 'review')
      return (
        <StepReview
          U={U}
          busy={busy}
          onSetReview={(patch) => dispatch({type: 'setReview', patch})}
          onBack={() => dispatch({type: 'back'})}
          onCommit={requestCommit}
        />
      )
    return <StepDone U={U} navigate={navigate} onAgain={() => dispatch({type: 'reset', fromCode})} />
  }

  return (
    <div className='uw-scope'>
      <PageTitle>Update Rates &amp; TAT</PageTitle>
      <WizardStyles />

      <SupplyHero
        icon={<AttachMoneyOutlinedIcon />}
        title='Update Rates & TAT'
        subtitle='Upload revised rate cards and delivery TAT for an account code — rate cards can be scheduled; TAT applies immediately.'
        actions={
          <Button variant='outlined' startIcon={<CloseIcon style={{fontSize: 16}} />} onClick={() => navigate('/supply/partners')} sx={HERO_BTN}>
            Cancel
          </Button>
        }
      />

      {total > 1 && k !== 'done' ? (
        <WizardStepper
          steps={real}
          current={stepIdx}
          contextLabel={(U.partnerName || 'No parent yet') + (U.code ? ' · ' + (U.display || U.code) : '') + ' · ' + scopeLbl}
        />
      ) : null}

      {renderStep()}

      <ConfirmDialog
        open={commitOpen}
        onClose={() => setCommitOpen(false)}
        title={'Upload to ' + (U.display || U.code) + '?'}
        body='Files are uploaded to ProShip for this account code. Rate cards with a future effective date are staged as pending; TAT applies immediately.'
        bullets={commitBullets}
        okLabel='Upload'
        onOk={() => {
          setCommitOpen(false)
          finish()
        }}
      />
    </div>
  )
}
